import * as React from 'react';
import { CLUB_INVITATIONS } from 'Src/consts/club';
import { SCHOOL_CLUB_INVITATIONS_TYPE } from 'Src/types/school';
import { Sport } from '../../../../../../../models/sport';
import { ClubSport } from '../../../../../../../models/club';
import { MultiSelectCheckboxItem } from '../../../../../../../components/MultiSelectCheckboxes/MultiSelectCheckboxes';
import '../ClubWizard.scss';
import Moment from 'moment';
import { DATE_FORMAT } from 'Src/consts/date';
import { AppUser } from 'Src/views/App/App';
import { getAgesGroupFormatted } from 'Src/helpers/accessor/accessor';

interface Props {
  maxParticipants: number;
  onInputChange: (event) => void;
  onInvitationsChange: (value: SCHOOL_CLUB_INVITATIONS_TYPE) => void;
  invitations: SCHOOL_CLUB_INVITATIONS_TYPE;
  user: AppUser;
  name: string;
  sport: Sport | ClubSport;
  genderItems: MultiSelectCheckboxItem[];
  ageItems: MultiSelectCheckboxItem[];
  start: Date;
  end: Date;
  dayItems: MultiSelectCheckboxItem[];
  time: string;
  duration: number;
  venue: any;
  scheduleMode: 'DATES' | 'TERMS' | 'DATES_LIST';
  termItems: MultiSelectCheckboxItem[];
  specificDates: Date[];
  isActive: boolean;
}

export function ClubWizardBookingStep(props: Props) {
  const {
    maxParticipants,
    onInputChange,
    onInvitationsChange,
    invitations,
    user,
    sport,
    genderItems,
    ageItems,
    start,
    end,
    dayItems,
    time,
    duration,
    venue,
    scheduleMode,
    termItems,
    isActive
  } = props;
  const checkedGenders = genderItems.filter(item => item.isChecked).map(item => item.name);
  const checkedAgeIndexes = ageItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.isChecked)
    .map(({ item, index }) => (typeof item.age === 'number' ? item.age : index));
  const checkedAges =
    checkedAgeIndexes.length === 0 || checkedAgeIndexes.length === ageItems.length
      ? 'All ages'
      : getAgesGroupFormatted({ ages: checkedAgeIndexes }, { user }).replace(/\s+-\s+/g, '-');
  const checkedDays = dayItems.filter(item => item.isChecked).map(item => item.name);
  const checkedTerms = termItems.filter(item => item.isChecked).length;
  const reviewRows = [
    ['Activity', (sport && sport.name) || 'Not set'],
    ['Audience', `${checkedGenders.join(' & ') || 'Not set'} · ${checkedAges || 'All ages'}`],
    [
      'Runs',
      scheduleMode === 'TERMS'
        ? `${checkedTerms} term${checkedTerms === 1 ? '' : 's'} selected`
        : scheduleMode === 'DATES_LIST'
        ? `${props.specificDates ? props.specificDates.length : 0} specific date${
            props.specificDates && props.specificDates.length === 1 ? '' : 's'
          } selected`
        : `${Moment(start).format(DATE_FORMAT)} – ${Moment(end).format(DATE_FORMAT)}`
    ],
    ['Sessions', `${checkedDays.join(', ') || 'Not set'} at ${time} · ${duration} min`],
    ['Venue', (venue && (venue.placeName || venue.name || venue.postcode)) || 'Not set'],
    ['Places', maxParticipants > 0 ? `${maxParticipants} students` : 'Unlimited']
  ];

  return (
    <div className="clubWizardDsStep">
      <section className="clubWizardDsSection">
        <div className="clubWizardDsSection__head">
          <span className="clubWizardDsSection__icon">1</span>
          <div>
            <h3>Booking</h3>
            <p>Set capacity and decide who booking forms are addressed to.</p>
          </div>
        </div>

        <div className="clubWizardDsGrid clubWizardDsGrid--two">
          <label className="clubWizardDsField">
            <span>Maximum number of students</span>
            <input type="text" onChange={onInputChange} value={maxParticipants} name="maxParticipants" disabled={isActive} />
            <small>Enter 0 for unlimited capacity.</small>
          </label>

          <div className="clubWizardDsField">
            <span>Booking forms are sent to</span>
            <div className="clubWizardDsSegmented">
              <button
                type="button"
                className={invitations === CLUB_INVITATIONS.PARENT ? 'isSelected' : ''}
                onClick={() => onInvitationsChange(CLUB_INVITATIONS.PARENT)}
                disabled={isActive}
              >
                Parents
              </button>
              <button
                type="button"
                className={invitations === CLUB_INVITATIONS.STUDENT ? 'isSelected' : ''}
                onClick={() => onInvitationsChange(CLUB_INVITATIONS.STUDENT)}
                disabled={isActive}
              >
                Students
              </button>
            </div>
            <small>Nothing is sent now. You send booking forms after the club is created.</small>
          </div>
        </div>

        <div className="clubWizardDsDivider" />
        <div className="clubWizardDsSection__head clubWizardDsReviewHead">
          <span className="clubWizardDsSection__icon">2</span>
          <div>
            <h3>Review</h3>
            <p>Check the club before you save.</p>
          </div>
        </div>
        <div className="clubWizardDsReview">
          {reviewRows.map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
